import { RootState } from "@/store";
import storeHelper from "@/store/store-helper";
import { Controllers, ProblemResult, IsBadRequestResult, IsServerErrorResult } from "@infoland/iprova-api";
import { ActionTree, Dispatch, GetterTree, Module, MutationTree } from "vuex";

import { GetMyTeams, Membership, Team } from "@/teams";

export interface AddTeamData {
    name: string;
    description: string;
    memberships: Membership[];
    manager_ids: string[];
}

export interface UpdateTeamData {
    team_id: string;
    name: string;
    description: string;
}

export interface MembershipData {
    team_id: string;
    memberships: Membership[];
}

export interface DeleteManagerData {
    team_id: string;
    managers: string[];
}

export interface AddManagerData {
    team_id: string;
    managers: string[];
}

export interface TeamsState {
    teams: Team[];
}

export interface CreateTeamResult {
    success: boolean;
    errorCode: number;
    teamId: string;
}

export interface ActionResult<S, E> {
    success: boolean;
    successData?: S;
    errorData?: E;
}

export interface TeamFilter {
    name_contains?: string;
    team_ids?: string[];
}

function initialize(): TeamsState {
    return {
        teams: []
    };
}

// initial state
const state: TeamsState = initialize();

// getters for this store
const getters: GetterTree<TeamsState, RootState> = {
    /**
     * Gets teams for the current user
     * @param state The state to read from
     */
    teams(state: TeamsState): Team[] {
        if (state.teams) {
            state.teams.sort((a, b) => a.name.localeCompare(b.name));
        }
        return state.teams;
    }
};

const mutations: MutationTree<TeamsState> = {
    teams(state: TeamsState, teams: Team[]): void {
        state.teams = teams;
    },
    addTeam(state: TeamsState, team: Team): void {
        state.teams.push(team);
    }
};

function handleTeamMutationResult(result: void | ProblemResult<string>, dispatch: Dispatch, reloadTeams: boolean = true): ActionResult<void, string> {
    if (IsServerErrorResult(result)) {
        storeHelper.siteStore.setSnackBarServerErrorMessage(result);
        return { success: false };
    } else if (IsBadRequestResult(result)) {
        if (result.extrainfo) {
            storeHelper.siteStore.setSnackBarMessageLookup("teams.errorMessages." + result.extrainfo, true);
        } else {
            storeHelper.siteStore.setSnackBarMessageLookup("general.messages.error", true);
        }

        return { success: false, errorData: result.extrainfo };
    } else {
        if (reloadTeams) dispatch("loadTeams");
        return { success: true };
    }
}

const actions: ActionTree<TeamsState, RootState> = {
    async loadTeams({ commit }): Promise<void> {
        //load teams from API and update state
        let teams = await GetMyTeams();

        //update store
        if (teams !== null) commit("teams", teams);
    },

    async createTeam({ dispatch, commit }, team: AddTeamData): Promise<ActionResult<string, string>> {
        const result = await Controllers.GenericController.postAsync<Team, string | ProblemResult<string>>("teams", <any>{
            name: team.name,
            description: team.description,
            memberships: team.memberships,
            manager_ids: team.manager_ids
        });
        const created_identifier: string = (result as any)?.created_identifier;
        if (created_identifier) {
            //add team to teams in state
            commit("addTeam", team);

            //update teams list to get any other change
            await dispatch("loadTeams");
            return {
                success: true,
                successData: created_identifier
            };
        } else if (IsBadRequestResult(result)) {
            return {
                success: false,
                errorData: result.extrainfo
            };
        }
    },

    async updateLastVisited({ state }, team_id: string): Promise<void> {
        await Controllers.GenericController.patchAsync<{ last_visited: string }, void>(`teams/${team_id}`, {
            last_visited: new Date().toAPIDateTimeString()
        });

        //set date directly in store
        const team = state.teams.find(t => t.team_id == team_id);
        if (team) team.last_visited = new Date();
    },

    async updateTeam({ state, dispatch }, team: UpdateTeamData): Promise<ActionResult<void, string>> {
        const result = await Controllers.GenericController.patchAsync<{ name: string; description: string }, void | ProblemResult<string>>(
            `teams/${team.team_id}`,
            {
                name: team.name,
                description: team.description
            }
        );
        const returnValue = handleTeamMutationResult(result, dispatch, false);

        if (returnValue.success) {
            //set name and description in store
            const storeTeam = state.teams.find(t => t.team_id == team.team_id);
            if (storeTeam) {
                storeTeam.name = team.name;
                storeTeam.description = team.description;
            }
        }
        return returnValue;
    },

    async deleteTeam({ dispatch }, team_id: string): Promise<ActionResult<void, string>> {
        const result = await Controllers.GenericController.deleteAsync<null, void | ProblemResult<string>>(`/teams/${team_id}`);

        return handleTeamMutationResult(result, dispatch);
    },

    async deleteMembership({ dispatch }, membershipData: MembershipData): Promise<ActionResult<void, string>> {
        const result = await Controllers.GenericController.deleteAsync<Membership[], void | ProblemResult<string>>(
            `/teams/${membershipData.team_id}/memberships`,
            membershipData.memberships
        );

        return handleTeamMutationResult(result, dispatch, true);
    },

    async addMembership({ dispatch }, membershipData: MembershipData): Promise<ActionResult<void, string>> {
        const result = await Controllers.GenericController.postAsync<Membership[], void | ProblemResult<string>>(
            `/teams/${membershipData.team_id}/memberships`,
            membershipData.memberships
        );

        return handleTeamMutationResult(result, dispatch, true);
    },

    async deleteManager({ dispatch }, managerData: DeleteManagerData): Promise<ActionResult<void, string>> {
        const result = await Controllers.GenericController.deleteAsync<string[], void | ProblemResult<string>>(
            `/teams/${managerData.team_id}/managers`,
            managerData.managers
        );

        return handleTeamMutationResult(result, dispatch);
    },

    async addManager({ dispatch }, managerData: AddManagerData): Promise<ActionResult<void, string>> {
        const result = await Controllers.GenericController.postAsync<string[], void | ProblemResult<string>>(
            `/teams/${managerData.team_id}/managers`,
            managerData.managers
        );

        return handleTeamMutationResult(result, dispatch);
    }
};

// export the namespace
export const namespace = "teams";

// export the store 'module'
export default <Module<TeamsState, RootState>>{
    namespaced: true,
    state,
    getters,
    mutations,
    actions
};
